import { ReactElement } from 'react';
import { Control, Controller, RegisterOptions } from 'react-hook-form';
import Select from 'react-select';

type Option = { value: string; label: string };

type Props = {
  id: string;
  label: string;
  options: Option[];
  noMarginTop?: boolean;
  validationObject?: RegisterOptions;
  control?: Control<any>;
  tooltip?: string;
};

/**
 * A dropdown react component.
 * @param props - DropdownProps.
 * @returns - React element to capture user selection.
 */
export default function MultiSelectDropdown(props: Props): ReactElement {
  const { label, options, noMarginTop, id, validationObject, control, tooltip } = props;
  return (
    <div className={`w-full ${!noMarginTop && 'mt-4'}`}>
      <div className="text-gray-500 dark:text-gray-500 text-sm flex items-center gap-1">
        <span>{label}</span>
        {validationObject?.required ? <span> *</span> : ''}
        {tooltip && (
          <div className="relative group/tooltip flex items-center">
            <span className="material-icons-outlined cursor-pointer text-base text-gray-400 leading-none">
              info
            </span>
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 z-50 w-60 p-2 text-xs text-white bg-gray-900 rounded-md transition-all duration-100 scale-0 origin-top group-hover/tooltip:scale-100">
              <p className="text-start">{tooltip}</p>
            </div>
          </div>
        )}
      </div>
      <Controller
        name={id}
        control={control}
        rules={validationObject}
        render={({ field, fieldState: { error } }) => (
          <>
            <Select
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...field}
              options={options}
              styles={{
                // eslint-disable-next-line jsdoc/require-jsdoc
                control: (styles) => ({
                  ...styles,
                  background: '#0d0d14',
                  marginTop: 8,
                  border: error ? '1px solid #FDA29B' : '1px solid #344054',
                  boxShadow: '0 !important',
                  '&:hover': {
                    border: '1px solid #84ADFF !important'
                  }
                }),
                // eslint-disable-next-line jsdoc/require-jsdoc
                menuList: (styles) => ({
                  ...styles,
                  background: '#12151f',
                  color: '#667085'
                }),
                // eslint-disable-next-line jsdoc/require-jsdoc
                option: (styles) => ({
                  ...styles,
                  background: '#12151f',
                  cursor: 'pointer',
                  '&:hover': {
                    background: '#1a1d2c',
                    color: '#D0D5DD'
                  }
                }),
                // eslint-disable-next-line jsdoc/require-jsdoc
                multiValue: (styles) => ({
                  ...styles,
                  background: '#1D2939',
                  color: '#B2CCFF'
                }),
                // eslint-disable-next-line jsdoc/require-jsdoc
                multiValueLabel: (styles) => ({
                  ...styles,
                  color: '#B2CCFF'
                })
              }}
              className="bg-container-500"
              isMulti
              closeMenuOnSelect={false}
            />
            <p className="text-error-500 mt-1"> {error?.message}</p>
          </>
        )}
      />
    </div>
  );
}
